import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import OutsideClickHandler from "react-outside-click-handler"
import {
  selectNotificationCounts,
  selectTotalNotificationCount
} from "lib/store/services/notifications/selectors"
import { selectUserGroups } from "lib/store/services/auth/selectors"
import { setOrderNavigation } from "lib/store/services/Order/orderSlice"
import { CustomStateType } from "lib/types/generated/graphql"
import { useConfig } from "configuration/useConfig"
import { Groups, PermissionGroups } from "routes/constants"
import { ReactComponent as BellIcon } from "images/icons/bell.svg"
import { ReactComponent as CircleExclamation } from "images/icons/circle-exclamation.svg"
import { ReactComponent as CircleCheck } from "images/icons/circle-check.svg"

import {
  BellWrapper,
  BellButton,
  BellIconWrapper,
  BadgeDot,
  BellText,
  BellMainText,
  BellSubText,
  DropdownWrapper,
  DropdownHeader,
  DropdownSubtitle,
  DropdownItem,
  ItemIcon,
  ItemContent,
  ItemTitle,
  ItemCount,
  EmptyState
} from "./NotificationBell.styled"

type Props = {
  collapsed: boolean
}

const formatBadgeCount = (count: number): string => {
  if (count > 99) return "99+"
  return String(count)
}

const NotificationBell = ({ collapsed }: Props) => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { config } = useConfig()
  const userGroups = useSelector(selectUserGroups)
  const counts = useSelector(selectNotificationCounts)
  const totalCount = useSelector(selectTotalNotificationCount)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isSettling, setIsSettling] = useState(false)

  const actionNeededEnabled = config.actionNeededEnabled
  const hasOpsRole = userGroups.some((g) =>
    (PermissionGroups.actionNeeded.operations as readonly string[]).includes(g)
  )
  const hasWorkflowsRole = userGroups.some((g) =>
    (PermissionGroups.actionNeeded.workflows as readonly string[]).includes(g)
  )
  const hasCustomStatesRole = userGroups.some(
    (g) => g === Groups.CX_USER || g === Groups.CX_ADMIN
  )
  const showOps = actionNeededEnabled && config.operationsEnabled && hasOpsRole
  const showWorkflows = actionNeededEnabled && config.workflowsEnabled && hasWorkflowsRole
  const showCustomStates = actionNeededEnabled && hasCustomStatesRole
  const prevCountRef = useRef(-1)
  const hasInitializedRef = useRef(false)

  useEffect(() => {
    if (!hasInitializedRef.current) {
      prevCountRef.current = totalCount
      hasInitializedRef.current = true
      return
    }
    if (totalCount > prevCountRef.current) {
      setIsAnimating(true)
    } else if (totalCount < prevCountRef.current) {
      setIsSettling(true)
    }
    prevCountRef.current = totalCount
  }, [totalCount])

  const handleNavigate = useCallback(
    (path: string) => {
      setOpen(false)
      navigate(path)
    },
    [navigate]
  )

  // The Orders page reads filters from Redux navigation state, not URL params.
  const handleNavigateToOrdersWithState = useCallback(
    (stateId: string, label: string) => {
      setOpen(false)
      dispatch(
        setOrderNavigation({
          navigation: { customState: { id: stateId, label } },
          filters: {
            fromDate: "",
            toDate: "",
            timeZone: "",
            storeGroups: [],
            countries: [],
            discountRules: [],
            focusedDiscountRule: null,
            shippingMethods: [],
            campaignGroups: [],
            campaigns: []
          }
        })
      )
      navigate("/orders")
    },
    [dispatch, navigate]
  )

  const items = useMemo(() => {
    const list: Array<{
      key: string
      title: string
      subtitle?: string
      count: number
      color: string
      icon: React.ReactElement
      onClick: () => void
    }> = []
    if (showOps) {
      list.push({
        key: "failed-operations",
        title: "Failed operations",
        count: counts.failedOps,
        color: "#cc3333",
        icon: <CircleExclamation />,
        onClick: () => handleNavigate("/dashboard/operations?filter=failed")
      })
      list.push({
        key: "approval-needed",
        title: "Approval needed",
        count: counts.approvalOps,
        color: "#F28C28",
        icon: <CircleCheck />,
        onClick: () => handleNavigate("/dashboard/operations?filter=approval")
      })
    }
    if (showWorkflows) {
      list.push({
        key: "failed-workflows",
        title: "Failed workflows",
        subtitle: "Last 7 days",
        count: counts.failedWorkflows,
        color: "#cc3333",
        icon: <CircleExclamation />,
        onClick: () => handleNavigate("/dashboard/workflows")
      })
    }
    if (showCustomStates) {
      counts.customStateCounts.forEach(({ stateId, label, type, count }) => {
        list.push({
          key: `custom-state-${stateId}`,
          title: label,
          subtitle: "Custom state",
          count,
          color: type === CustomStateType.Blocking ? "#cc3333" : "#F28C28",
          icon: type === CustomStateType.Blocking ? <CircleExclamation /> : <CircleCheck />,
          onClick: () => handleNavigateToOrdersWithState(stateId, label)
        })
      })
    }
    return list
  }, [
    counts,
    showOps,
    showWorkflows,
    showCustomStates,
    handleNavigate,
    handleNavigateToOrdersWithState
  ])

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current)
    }
  }, [])

  const handleMouseEnter = () => {
    if (collapsed) {
      cancelClose()
      setOpen(true)
    }
  }

  if (items.length === 0) return null

  const handleMouseLeave = () => {
    if (collapsed) {
      cancelClose()
      closeTimer.current = setTimeout(() => setOpen(false), 150)
    }
  }

  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <BellWrapper
        $collapsed={collapsed}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <BellButton
          onClick={() => setOpen(!open)}
          aria-label="Action Needed"
          data-testid="notification-bell"
          $collapsed={collapsed}
        >
          <BellIconWrapper
            $animate={isAnimating}
            onAnimationEnd={() => setIsAnimating(false)}
          >
            <BellIcon />
            {totalCount > 0 && (
              <BadgeDot
                $animate={isAnimating}
                $settle={isSettling}
                onAnimationEnd={() => setIsSettling(false)}
                data-testid="notification-indicator"
              >
                {formatBadgeCount(totalCount)}
              </BadgeDot>
            )}
          </BellIconWrapper>
          <BellText $collapsed={collapsed}>
            <BellMainText>Action Needed</BellMainText>
            <BellSubText>
              {totalCount > 0 ? `${totalCount} need attention` : "All clear"}
            </BellSubText>
          </BellText>
        </BellButton>

        {open && (
          <DropdownWrapper
            data-testid="notification-dropdown"
            $collapsed={collapsed}
            onMouseEnter={cancelClose}
            onMouseLeave={handleMouseLeave}
          >
            <DropdownHeader>
              <span>Action Needed</span>
            </DropdownHeader>
            {totalCount === 0 ? (
              <EmptyState>No items need attention</EmptyState>
            ) : (
              items.map((item) => (
                <DropdownItem
                  key={item.key}
                  $hasCount={item.count > 0}
                  onClick={() => {
                    if (item.count <= 0) return
                    item.onClick()
                  }}
                  data-testid={`notification-item-${item.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <ItemIcon $color={item.count > 0 ? item.color : "#444444"}>
                    {item.icon}
                  </ItemIcon>
                  <ItemContent>
                    <ItemTitle>{item.title}</ItemTitle>
                    {"subtitle" in item && item.subtitle && (
                      <DropdownSubtitle>{item.subtitle}</DropdownSubtitle>
                    )}
                  </ItemContent>
                  <ItemCount>{item.count}</ItemCount>
                </DropdownItem>
              ))
            )}
          </DropdownWrapper>
        )}
      </BellWrapper>
    </OutsideClickHandler>
  )
}

export default NotificationBell
